import React, { useEffect } from 'react';
import clsx from 'clsx';
import InputField from '../../Forms/Input/InputField';
import TextAreaField from '../../Forms/Input/TextAreaField';
import SelectField from '../../Forms/Input/SelectField';
import { ICustomerForm } from '../../../types/customer/customerInterfaces';
import { useCustomer } from '../../../hooks/useCustomer';
import SimpleSearchSelect from '../../Forms/Input/SimpleSearchSelect';

interface CustomerConsumidorFinalFormProps {
  form: ICustomerForm;

  updateField: (field: keyof ICustomerForm, value: any) => void;
  countryDropdown: { id: number; name: string }[];
  departments: { id: number; name: string; code: string }[];
  municipalities: { id: number; name: string }[];
  documentIdentificationTypeDropdown: {
    id: number;
    name: string;
    code: string;
  }[];
  onFetchMunicipalities: (departmentCode: string) => void;
  isSubmitting?: boolean;
  onCancel?: () => void;
  onSubmit?: () => void;
}

const CustomerConsumidorFinalForm: React.FC<
  CustomerConsumidorFinalFormProps
> = ({
  form,
  updateField,
  countryDropdown,
  departments,
  municipalities,
  documentIdentificationTypeDropdown,
  onFetchMunicipalities,
  isSubmitting = false,
  onCancel,
  onSubmit,
}) => {
  const handleDepartmentChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = Number(e.target.value);
    updateField('departmentId', selectedId);
    updateField('municipalityId', 0);
    const dept = departments.find((d) => d.id === selectedId);
    if (dept) onFetchMunicipalities(dept.code);
  };

  useEffect(() => {
    if (form.departmentId && form.departmentCode) {
      onFetchMunicipalities(form.departmentCode);
    }
  }, [form.departmentId, form.departmentCode]);

  const { errors, clearIdentification } = useCustomer();

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      {/* 📌 Tipo de Documento */}
      <SelectField
        label="Tipo de Documento"
        name="documentIdentificationTypeId"
        iconKey="list"
        value={form.documentIdentificationTypeId ?? ''}
        onChange={(e) => {
          const selectedId = Number(e.target.value);
          updateField('documentIdentificationTypeId', selectedId);

          // 👉 Busca el código asociado al ID seleccionado
          const selected = documentIdentificationTypeDropdown.find(
            (t) => t.id === selectedId
          );
          updateField('documentIdentificationTypeCode', selected?.code || '');

          // ✅ Limpiar todos los campos de identificación anteriores
          clearIdentification();
        }}
        options={documentIdentificationTypeDropdown.map((item) => ({
          value: item.id,
          label: item.name,
        }))}
        error={errors?.documentIdentificationTypeId}
      />

      {(() => {
        const selected = documentIdentificationTypeDropdown.find(
          (t) => t.id === form.documentIdentificationTypeId
        );
        const selectedCode = selected?.code;

        return (
          <>
            {/* Nombre */}
            <InputField
              label="Nombre"
              name="name"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              type="text"
              placeholder="Ingrese el nombre completo"
              iconKey="user"
              error={errors?.name}
            />

            {/* Condicionales por código */}
            {selectedCode === '36' && (
              <InputField
                label="NIT"
                name="nit"
                value={form.nit}
                onChange={(e) => updateField('nit', e.target.value)}
                type="text"
                placeholder="0000-000000-000-0"
                iconKey="idCard"
                error={errors?.nit}
              />
            )}

            {selectedCode === '13' && (
              <InputField
                label="DUI"
                name="dui"
                value={form.dui}
                onChange={(e) => updateField('dui', e.target.value)}
                type="text"
                placeholder="00000000-0"
                iconKey="idCard"
                error={errors?.dui}
              />
            )}

            {selectedCode === '03' && (
              <InputField
                label="Pasaporte"
                name="passportNumber"
                value={form.passportNumber}
                onChange={(e) => updateField('passportNumber', e.target.value)}
                type="text"
                iconKey="idCard"
                error={errors?.passportNumber}
              />
            )}

            {selectedCode === '02' && (
              <InputField
                label="Carnet de Residente"
                name="residentCardNumber"
                value={form.residentCardNumber}
                onChange={(e) =>
                  updateField('residentCardNumber', e.target.value)
                }
                type="text"
                iconKey="idCard"
                error={errors?.residentCardNumber}
              />
            )}

            {selectedCode === '37' && (
              <InputField
                label="Otra Identificación"
                name="otherIdentification"
                value={form.otherIdentification}
                onChange={(e) =>
                  updateField('otherIdentification', e.target.value)
                }
                iconKey="edit"
                type="text"
                error={errors?.otherIdentification}
              />
            )}
          </>
        );
      })()}

      {/* 📌 Teléfono y Correo siempre visibles */}
      <InputField
        label="Teléfono"
        name="phone"
        value={form.phone}
        onChange={(e) => updateField('phone', e.target.value)}
        type="text"
        placeholder="Ejemplo: 7777-7777"
        iconKey="phone"
        error={errors?.phone}
      />

      <InputField
        label="Correo Electrónico"
        name="email"
        type="email"
        value={form.email}
        onChange={(e) => updateField('email', e.target.value)}
        placeholder="usuario@correo.com"
        iconKey="email"
        error={errors?.email}
      />

      {/* 🌍 Sección: País */}
      <div className="mt-5 md:col-span-2">
        <div className="mb-6 border-b pb-4">
          <h2 className="text-gray-800 dark:text-gray-200 mb-1 text-xl font-bold">
            País
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Selecciona el país de residencia del cliente (opcional).
          </p>
        </div>

        <div className="max-w-md">
          <SimpleSearchSelect
            label="País"
            name="countryId"
            options={countryDropdown}
            selectedValue={form.countryId}
            onSelect={(option) => updateField('countryId', option.id)}
            iconKey='globe'
          />
        </div>
      </div>

      {/* 📌 Dirección */}
      <div className="mt-5 md:col-span-2">
        <div className="mb-6 border-b pb-4">
          <h2 className="text-gray-800 dark:text-gray-200 mb-1 text-xl font-bold">
            Dirección
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            Selecciona el departamento y municipio del cliente, luego ingresa la
            dirección completa.
          </p>
        </div>

        <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
          <SelectField
            label="Departamento"
            name="departmentId"
            value={form.departmentId}
            onChange={handleDepartmentChange}
            options={departments.map((d) => ({
              value: d.id,
              label: d.name,
            }))}
            iconKey="list"
            error={errors?.departmentId}
          />

          <SelectField
            label="Municipio"
            name="municipalityId"
            value={form.municipalityId}
            onChange={(e) =>
              updateField('municipalityId', Number(e.target.value))
            }
            options={municipalities.map((m) => ({
              value: m.id,
              label: m.name,
            }))}
            iconKey="list"
            error={errors?.municipalityId}
          />
        </div>

        <div className="mt-6">
          <TextAreaField
            label="Complemento"
            name="complement"
            value={form.complement}
            onChange={(e) => updateField('complement', e.target.value)}
            iconKey="address"
            error={errors?.complement}
          />
        </div>
      </div>

      {(onCancel || onSubmit) && (
        <div className="mt-6 flex gap-4 md:col-span-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 rounded bg-[#DC3545] px-4.5 py-2.5 font-medium text-white hover:bg-opacity-90"
            >
              Cancelar
            </button>
          )}
          {onSubmit && (
            <button
              type="button"
              disabled={isSubmitting}
              onClick={onSubmit}
              className={clsx(
                'flex-1 rounded px-4.5 py-2.5 font-medium text-white',
                isSubmitting
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primary hover:bg-opacity-90'
              )}
            >
              {isSubmitting
                ? form.id
                  ? 'Actualizando...'
                  : 'Guardando...'
                : form.id
                ? 'Actualizar Cliente'
                : 'Registrar Cliente'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerConsumidorFinalForm;
